import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import { DatePicker } from '../../ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { Separator } from '../../ui/separator'
import { Badge } from '../../ui/badge'
import { Checkbox } from '../../ui/checkbox'
import { Clock, FileText } from 'lucide-react'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import { useEventContext } from '../EventContext'
import { DateTime } from 'luxon'
import { toast } from 'sonner'

const timezones = [
  { value: 'America/Toronto', label: 'Eastern Time' },
  { value: 'America/Vancouver', label: 'Pacific Time' },
  { value: 'America/Calgary', label: 'Mountain Time' },
  { value: 'America/Winnipeg', label: 'Central Time' },
  { value: 'America/Halifax', label: 'Atlantic Time' },
  { value: 'America/St_Johns', label: 'Newfoundland Time' },
]

const daysOfWeek = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
]

export const EventBasicInfo: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { event, setEvent, canEdit } = useEventContext()

  const updateEventField = async (field: string, value: any) => {
    if (!eventId) return

    try {
      const updatedEvent = await EventEndpoints.editEvent(parseInt(eventId), {
        field,
        value,
      })
      setEvent(updatedEvent)
      toast.success(`${field} updated successfully`)
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error occurred'
      toast.error(`Failed to update ${field}: ${errorMsg}`)
    }
  }

  if (!event || !eventId) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading event...</p>
      </div>
    )
  }

  const handleFieldUpdate = async (field: string, value: string | number) => {
    await updateEventField(field, value)
  }

  const selectedTimezone = timezones.find((tz) => tz.value === event.timezone)
  const clientTimezone = timezones.find((tz) => tz.value === event.client?.timezone)
  const clientDefaultLabel = clientTimezone
    ? `Client Default (${clientTimezone.label})`
    : 'Client Default'
  const selectedFirstDay = daysOfWeek.find(
    (day) => day.value === event.firstDayOfWeek?.toString()
  )

  return (
    <div className="space-y-6">
      {/* Event Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Event Overview
          </CardTitle>
          <CardDescription>
            Basic information and settings for this event
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="eventName">Event Name</Label>
              <Input
                id="eventName"
                defaultValue={event.name}
                onBlur={(e) => handleFieldUpdate('name', e.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label>Client</Label>
              <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
                <Badge variant="secondary">{event.client?.name}</Badge>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <DatePicker
                date={
                  event.startDate
                    ? typeof event.startDate === 'string' &&
                      event.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
                      ? DateTime.fromFormat(
                          event.startDate,
                          'yyyy-MM-dd'
                        ).toJSDate()
                      : new Date(event.startDate)
                    : undefined
                }
                onDateChange={(date) => {
                  if (date) {
                    const normalizedDate = DateTime.fromJSDate(date)
                      .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
                      .toJSDate()
                    handleFieldUpdate('startDate', normalizedDate.getTime())
                  }
                }}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <DatePicker
                date={
                  event.endDate
                    ? typeof event.endDate === 'string' &&
                      event.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
                      ? DateTime.fromFormat(
                          event.endDate,
                          'yyyy-MM-dd'
                        ).toJSDate()
                      : new Date(event.endDate)
                    : undefined
                }
                onDateChange={(date) => {
                  if (date) {
                    const normalizedDate = DateTime.fromJSDate(date)
                      .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
                      .toJSDate()
                    handleFieldUpdate('endDate', normalizedDate.getTime())
                  }
                }}
                disabled={!canEdit}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Event notes and additional information..."
              defaultValue={event.notes || ''}
              onBlur={(e) => handleFieldUpdate('notes', e.target.value)}
              disabled={false}
              rows={4}
            />
          </div>

          <Separator />

          <div className="flex items-start space-x-2">
            <Checkbox
              id="publicAccreditation"
              checked={event.publicAccreditationEnabled === true}
              onCheckedChange={(checked) =>
                handleFieldUpdate(
                  'publicAccreditationEnabled',
                  checked === true ? 'true' : 'false'
                )
              }
              disabled={!canEdit}
            />
            <div className="space-y-1">
              <label
                htmlFor="publicAccreditation"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Public accreditation registration
              </label>
              <p className="text-xs text-muted-foreground">
                Terms &amp; conditions agreements from the public accreditation
                form are recorded against this event. Enable this on only one
                current event per client.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Schedule Settings
          </CardTitle>
          <CardDescription>
            Configure scheduling and time zone settings for this event
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="timezone">Time Zone</Label>
              <Select
                value={event.timezone || 'client-default'}
                onValueChange={(value) => handleFieldUpdate('timezone', value === 'client-default' ? '' : value)}
                disabled={!canEdit}
              >
                <SelectTrigger id="timezone">
                  <SelectValue placeholder="Select time zone">
                    {event.timezone ? (selectedTimezone?.label || event.timezone) : clientDefaultLabel}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client-default">{clientDefaultLabel}</SelectItem>
                  {timezones.map((timezone) => (
                    <SelectItem key={timezone.value} value={timezone.value}>
                      {timezone.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstDay">First Day of Week</Label>
              <Select
                value={event.firstDayOfWeek?.toString() || '0'}
                onValueChange={(value) =>
                  handleFieldUpdate('firstDayOfWeek', value)
                }
                disabled={!canEdit}
              >
                <SelectTrigger id="firstDay">
                  <SelectValue>
                    {selectedFirstDay?.label || 'Sunday'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="overtime">Overtime Threshold (hours)</Label>
              <Input
                id="overtime"
                type="number"
                min="0"
                max="100"
                step="1"
                defaultValue={event.overtimeThreshold || 8}
                onBlur={(e) =>
                  handleFieldUpdate('overtimeThreshold', e.target.value)
                }
                disabled={!canEdit}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
